/**
 * Subscription Gate Component
 *
 * Wraps protected content and shows a subscription prompt
 * if the user doesn't have an active subscription.
 */

import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Check as CheckIcon,
  Lock as LockIcon,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../contexts/SubscriptionContext';
import billingApi from '../utils/billingApi';

/**
 * Features list for the subscription prompt
 */
const FEATURES = [
  'Unlimited appeal generation',
  'AI-powered claim analysis',
  'Document extraction & management',
  'Appeal letter customization',
  'Priority support',
];

/**
 * Subscription Gate - wraps content that requires an active subscription
 *
 * @param {React.ReactNode} children - Content to show if subscribed
 * @param {string} featureName - Name of the feature being gated (for messaging)
 */
const SubscriptionGate = ({ children, featureName = 'this feature' }) => {
  const navigate = useNavigate();
  const { isActive, loading, error, subscription, lastBlockedPayload } = useSubscription();
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);

  const subscriptionStatus = lastBlockedPayload?.subscription_status || subscription?.status || null;
  const hasSubscription = lastBlockedPayload?.has_subscription ?? subscription?.has_subscription ?? false;

  const recoveryMessage = (() => {
    if (subscriptionStatus === 'past_due' || subscriptionStatus === 'unpaid') {
      return 'Your premium access is paused because billing needs attention. Update your payment details to continue.';
    }

    if (subscriptionStatus === 'canceled') {
      return 'Your subscription has been canceled. Reactivate billing to continue using this premium feature.';
    }

    if (hasSubscription) {
      return `Your current subscription status (${subscriptionStatus}) does not include access to ${featureName}.`;
    }

    return `Subscribe to AppealBot to access ${featureName} and all premium features.`;
  })();

  /**
   * Handle subscribe button - redirect to Stripe Checkout
   */
  const handleSubscribe = async () => {
    try {
      setActionLoading(true);
      setActionError(null);
      const data = await billingApi.createCheckoutSession();
      window.location.href = data.url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setActionError('Failed to start checkout. Please try again.');
      setActionLoading(false);
    }
  };

  /**
   * Handle manage subscription - redirect to Stripe Portal
   */
  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      setActionError(null);
      const data = await billingApi.createPortalSession();
      window.location.href = data.url;
    } catch (err) {
      console.error('Error creating portal session:', err);
      setActionError('Failed to open billing portal. Please try again.');
      setActionLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Checking subscription status...
        </Typography>
      </Container>
    );
  }

  // Error loading subscription - show error but allow retry
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={() => window.location.reload()}>
          Retry
        </Button>
      </Container>
    );
  }

  // Active subscription - render children
  if (isActive()) {
    return <>{children}</>;
  }

  // No active subscription - show paywall
  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <LockIcon size={48} />
        </Box>

        <Typography variant="h5" gutterBottom>
          Subscription Required
        </Typography>

        <Typography variant="body1" color="text.secondary" paragraph>
          {recoveryMessage}
        </Typography>

        {lastBlockedPayload?.error_type === 'subscription_required' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Your last premium request was blocked by the server. Refresh your billing state below and try again after your subscription is active.
          </Alert>
        )}

        {actionError && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
            {actionError}
          </Alert>
        )}

        <Card
          elevation={0}
          sx={{
            border: 2,
            borderColor: 'primary.main',
            mb: 3,
            textAlign: 'left',
          }}
        >
          <CardContent>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                AppealBot Monthly
              </Typography>
              <Typography variant="h3" color="primary">
                $29.99
                <Typography component="span" variant="body2" color="text.secondary">
                  /month
                </Typography>
              </Typography>
            </Box>

            <List dense>
              {FEATURES.map((feature, index) => (
                <ListItem key={index} disableGutters>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckIcon size={16} color="var(--ab-approved)" />
                  </ListItemIcon>
                  <ListItemText primary={feature} />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<CreditCardIcon size={16} />}
            onClick={hasSubscription ? handleManageSubscription : handleSubscribe}
            disabled={actionLoading}
            fullWidth
          >
            {actionLoading ? 'Redirecting...' : hasSubscription ? 'Manage Billing' : 'Subscribe Now'}
          </Button>

          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/billing')}
          >
            View billing details
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SubscriptionGate;
